import React, { memo, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { cloneDeep } from 'lodash'
import {
  ISpotBiddingRule,
  ISpotBiddingConfig,
  SPOT_PREMIUM_TYPE,
  RULE_ACTION,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'
import {
  closeAddSpotBiddingRuleModal,
  createSpotBiddingRule,
  updateSpotBiddingRule,
} from 'apps/spot-quote-control-panel/utils/actions/spotBiddingConfig'
import { validateRule } from 'apps/spot-quote-control-panel/utils/validator'
import { FormModal } from 'transfix-ui'
import {
  RuleType,
  GeoType,
  GeoValue,
  LaneGeoValue,
  CountLoadsQuotedValue,
  Premium,
  Mileage,
  DistanceToMarketCenter,
  AwardType,
  LocationType,
  IsDrop,
  IsMultiStop,
  DateType,
  DateRange,
  NumDaysOut,
  Equipment,
  ApplicableDuring,
  StopSpecificDrop,
  TransitDifference,
  IsSameDay,
  RuleStartTime,
} from './form-components'
import { AppointmentTime } from './form-components/AppointmentTime'
import { CarrierWaitTime } from './form-components/CarrierWaitTime'
import { ConditionQualifier } from './form-components/ConditionQualifier'
import { OnlyBidOnOption } from './form-components/OnlyBidOnOption'
import { PremiumType } from './form-components/PremiumType'
import { Reason } from './form-components/Reason'
import { RuleOption } from './form-components/RuleOption'

interface ISpotBiddingRuleModalProps {
  showModal: boolean
  errors: string
  spotBiddingConfig: ISpotBiddingConfig
  isGlobal: boolean
  rule?: ISpotBiddingRule
  saving: boolean
}

const defaultState: RecursivePartial<ISpotBiddingRule> = {
  ruleAction: undefined,
  premiumType: SPOT_PREMIUM_TYPE.percentage,
  conditionQualifier: undefined,
  spotPremium: undefined,
  geographicCriteria: undefined,
  mileageCriteria: undefined,
  laneGeographicCriteria: undefined,
  proximalHourCriteria: undefined,
  appointmentTimeCriteria: undefined,
  onlyBidOnCriteria: undefined,
  dateCriteria: undefined,
  isBaseline: false,
  isSameDay: false,
  isWeekend: false,
  transitDifferenceCriteria: undefined,
  ruleStartTimeCriteria: undefined,
  reason: undefined,
}

interface IValidation {
  pristine: boolean
  errors: { [key: string]: string }
}

const initialValidation = {
  pristine: true,
  errors: {},
} as IValidation

export const AddSpotBiddingRuleModal = memo<ISpotBiddingRuleModalProps>(
  ({ errors, isGlobal, rule: ruleToEdit, saving, showModal, spotBiddingConfig }) => {
    const [formState, setFormState] = useState(defaultState)
    const {
      appointmentTimeCriteria,
      carrierWaitTimeCriteria,
      contractCriteria,
      countLoadsQuotedCriteria,
      dateCriteria,
      distanceToMarketCenterCriteria,
      geographicCriteria,
      isDifferentPickupDelivery,
      isDrop,
      isMultiStop,
      isSameDay,
      isSamePickupDelivery,
      isWeekend,
      laneGeographicCriteria,
      mileageCriteria,
      onlyBidOnCriteria,
      parentEquipmentKey,
      proximalHourCriteria,
      reason,
      ruleAction,
      ruleStartTimeCriteria,
      spotPremium,
      stopSpecificDropCriteria,
      transitDifferenceCriteria,
    } = formState
    const dispatch = useDispatch()
    const [validation, setValidation] = useState<IValidation>(initialValidation)

    useEffect(() => {
      if (showModal) {
        setFormState(ruleToEdit ? cloneDeep(ruleToEdit) : defaultState)
      }
      setValidation({ pristine: true, errors: {} })
    }, [showModal])

    const validate = (rule: RecursivePartial<ISpotBiddingRule>) => {
      try {
        validateRule(rule)
      } catch (errs) {
        setValidation({ pristine: false, errors: errs })
        return false
      }
      setValidation({ pristine: false, errors: {} })
      return true
    }
    const formStateChangeHandler = (change: RecursivePartial<ISpotBiddingRule>) =>
      setFormState(current => {
        const nextState = { ...current, ...change }
        if (!validation.pristine) {
          validate(nextState)
        }
        return nextState
      })

    const closeModalHandler = () => {
      dispatch(closeAddSpotBiddingRuleModal())
      setFormState(defaultState)
    }

    const ruleDetailLabel =
      ruleAction === RULE_ACTION.onlyBidOn
        ? 'Only bid on loads that match this criteria'
        : 'Values & Premium'

    const submitForm = () => {
      if (validate(formState)) {
        if (ruleToEdit) {
          dispatch(updateSpotBiddingRule(spotBiddingConfig, formState))
        } else {
          dispatch(createSpotBiddingRule(spotBiddingConfig, formState))
        }
      }
    }

    const ruleTypeSelected =
      formState.isBaseline ||
      mileageCriteria ||
      distanceToMarketCenterCriteria?.locationType ||
      geographicCriteria?.criteriaType ||
      laneGeographicCriteria ||
      countLoadsQuotedCriteria ||
      contractCriteria ||
      isSameDay ||
      isWeekend ||
      dateCriteria ||
      proximalHourCriteria ||
      parentEquipmentKey ||
      carrierWaitTimeCriteria ||
      isDrop ||
      isMultiStop ||
      stopSpecificDropCriteria ||
      transitDifferenceCriteria ||
      isSamePickupDelivery ||
      isDifferentPickupDelivery ||
      ruleStartTimeCriteria

    const dateTypeCriteria = isSameDay || isWeekend || dateCriteria || proximalHourCriteria
    const showIsSameDay = formState.ruleAction !== RULE_ACTION.onlyBidOn && !!proximalHourCriteria
    const showIsDrop =
      formState.ruleAction !== RULE_ACTION.onlyBidOn &&
      (!!mileageCriteria ||
        !!isSameDay ||
        !!proximalHourCriteria ||
        !!geographicCriteria?.criteriaType ||
        !!countLoadsQuotedCriteria)

    const showIsMultiStop =
      formState.ruleAction !== RULE_ACTION.onlyBidOn &&
      (!!mileageCriteria ||
        !!isSameDay ||
        !!proximalHourCriteria ||
        !!geographicCriteria?.criteriaType ||
        !!transitDifferenceCriteria)

    const choosePremiumType = spotPremium && !!mileageCriteria

    const onlyBidOnRule = ruleAction === RULE_ACTION.onlyBidOn

    return (
      <FormModal
        title={`${ruleToEdit ? 'Edit' : 'Add'} Rule`}
        size={6}
        open={showModal}
        onClose={closeModalHandler}
        dataTest='modal-edit-shipper-rate'
        error={errors}
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            dataTest: 'cancel-spot-bidding-rule-button',
            disabled: saving,
          },
          {
            label: 'Save',
            type: 'confirm',
            action: submitForm,
            dataTest: 'save-spot-bidding-rule-button',
            disabled: saving || !ruleTypeSelected,
          },
        ]}>
        {showModal && (
          <>
            <p>
              <b>Select an option to start</b>
            </p>
            <div className='row'>
              <RuleOption value={formState} onChange={formStateChangeHandler} />
              {ruleAction && (
                <RuleType value={formState} onChange={formStateChangeHandler} isGlobal={isGlobal} />
              )}
              {geographicCriteria && (
                <>
                  <GeoType value={geographicCriteria} onChange={formStateChangeHandler} />
                </>
              )}
              {dateTypeCriteria && !geographicCriteria && (
                <DateType value={formState} onChange={formStateChangeHandler} />
              )}
              {distanceToMarketCenterCriteria && (
                <LocationType
                  value={distanceToMarketCenterCriteria}
                  onChange={formStateChangeHandler}
                />
              )}
            </div>
            {ruleTypeSelected && (
              <>
                <div>
                  <p>
                    <b>{ruleDetailLabel}</b>
                  </p>
                  {geographicCriteria?.criteriaType && (
                    <GeoValue
                      value={geographicCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.geographicCriteria}
                    />
                  )}
                  {laneGeographicCriteria && (
                    <LaneGeoValue
                      value={laneGeographicCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.laneGeographicCriteria}
                    />
                  )}
                  {contractCriteria && (
                    <AwardType
                      value={contractCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.contractCriteria}
                    />
                  )}
                  {countLoadsQuotedCriteria && (
                    <CountLoadsQuotedValue
                      value={countLoadsQuotedCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.countLoadsQuotedCriteria}
                    />
                  )}
                  {!appointmentTimeCriteria && mileageCriteria && (
                    <Mileage
                      value={mileageCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.mileageCriteria}
                    />
                  )}
                  {distanceToMarketCenterCriteria && (
                    <DistanceToMarketCenter
                      value={distanceToMarketCenterCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.distanceToMarketCenterCriteria}
                    />
                  )}
                  {dateCriteria && (
                    <DateRange
                      value={dateCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.dateCriteria}
                    />
                  )}
                  {isSameDay && !proximalHourCriteria && (
                    <RuleStartTime
                      value={ruleStartTimeCriteria}
                      onChange={formStateChangeHandler}
                    />
                  )}
                  {!geographicCriteria && proximalHourCriteria && (
                    <NumDaysOut
                      value={proximalHourCriteria}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.proximalHourCriteria}
                    />
                  )}
                  {showIsSameDay && (
                    <IsSameDay
                      value={formState}
                      onChange={formStateChangeHandler}
                      dataTest='same-day-checkbox-row'
                    />
                  )}
                  {appointmentTimeCriteria && (
                    <AppointmentTime value={formState} onChange={formStateChangeHandler} />
                  )}
                  {carrierWaitTimeCriteria && (
                    <CarrierWaitTime
                      value={carrierWaitTimeCriteria}
                      onChange={formStateChangeHandler}
                    />
                  )}
                  {parentEquipmentKey && (
                    <Equipment value={parentEquipmentKey} onChange={formStateChangeHandler} />
                  )}
                  {showIsDrop && (
                    <IsDrop
                      value={formState}
                      onChange={formStateChangeHandler}
                      dataTest='drop-checkbox-row'
                    />
                  )}

                  {stopSpecificDropCriteria && (
                    <StopSpecificDrop
                      value={stopSpecificDropCriteria}
                      onChange={formStateChangeHandler}
                    />
                  )}
                  {showIsMultiStop && (
                    <IsMultiStop
                      value={formState}
                      onChange={formStateChangeHandler}
                      dataTest='multistop-checkbox-row'
                    />
                  )}
                  {transitDifferenceCriteria && isMultiStop && (
                    <TransitDifference
                      value={transitDifferenceCriteria}
                      onChange={formStateChangeHandler}
                    />
                  )}
                  {choosePremiumType && (
                    <PremiumType value={formState} onChange={formStateChangeHandler} />
                  )}
                  {spotPremium && (
                    <Premium
                      value={spotPremium}
                      onChange={formStateChangeHandler}
                      error={validation.errors?.spotPremium}
                    />
                  )}
                </div>
                {geographicCriteria?.criteriaType && (
                  <>
                    <div className='row'>
                      <ApplicableDuring value={formState} onChange={formStateChangeHandler} />
                    </div>
                    {proximalHourCriteria && (
                      <div className='row'>
                        <NumDaysOut
                          value={proximalHourCriteria}
                          onChange={formStateChangeHandler}
                          error={validation.errors?.proximalHourCriteria}
                        />
                      </div>
                    )}
                  </>
                )}
                <div className='row'>
                  <Reason value={reason} onChange={formStateChangeHandler} />
                </div>
              </>
            )}
          </>
        )}
        {onlyBidOnRule && (
          <>
            <p>
              <b>If the load ...</b>
            </p>
            <ConditionQualifier
              value={formState}
              onChange={formStateChangeHandler}
              error={validation.errors?.conditionQualifier}
            />
            <OnlyBidOnOption
              value={onlyBidOnCriteria}
              rule={formState}
              onChange={formStateChangeHandler}
              error={validation.errors?.onlyBidOnCriteria}
            />
          </>
        )}
      </FormModal>
    )
  }
)
